import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { getReportData } from "../crud";
import { ReportDataRequest, ReportColumn } from "../types";
import { trackReportUsage } from "../reportUsageTracker";
import { format, startOfMonth, addDays, eachDayOfInterval, parse } from "date-fns";
import { useAuth } from "../../../modules/auth";
import ColumnSelectorModal from "../components/ColumnSelectorModal";

// --- Pivot data structures ---
interface PivotCell {
  treatments: number;
  revenue: number;
}

interface PivotRow {
  branchName: string;
  treatmentName: string;
  cells: Record<string, PivotCell>;
  grandTotal: PivotCell;
}

interface BranchGroup {
  branchName: string;
  rows: PivotRow[];
  branchTotal: Record<string, PivotCell>;
  branchGrandTotal: PivotCell;
}

interface PivotResult {
  dates: string[];
  branches: BranchGroup[];
  grandTotal: Record<string, PivotCell>;
  grandGrandTotal: PivotCell;
}

const REPORT_ID = 18;

const formatDateStr = (d: Date) => format(d, "yyyy-MM-dd");

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const formatNumber = (value: number): string =>
  new Intl.NumberFormat("en-IN").format(value);

const formatDateHeader = (dateStr: string): string => {
  try {
    const d = parse(dateStr, "yyyy-MM-dd", new Date());
    return format(d, "dd MMM");
  } catch {
    return dateStr;
  }
};

// Column definitions for the selector modal
const PIVOT_COLUMNS: ReportColumn[] = [
  { field: "treatments", displayName: "Treatment Count", type: "number", sortable: false, filterable: false, visible: true, includeInTotals: true, width: 50 },
  { field: "revenue", displayName: "Revenue", type: "currency", sortable: false, filterable: false, visible: true, includeInTotals: true, width: 50 },
];

const DEFAULT_VISIBLE = ["treatments", "revenue"];

// Build the pivot from flat data
function buildPivot(
  rawData: Array<Record<string, any>>,
  dateFrom: Date,
  dateTo: Date
): PivotResult {
  // Generate all dates in range
  const allDates = eachDayOfInterval({ start: dateFrom, end: dateTo }).map((d) =>
    formatDateStr(d)
  );

  // Build nested map: branch -> treatment -> date -> cell
  const branchMap = new Map<string, Map<string, Map<string, PivotCell>>>();

  for (const row of rawData) {
    const branch = row.branchName || "Unknown Branch";
    const treatment = row.treatmentName || "Unknown";
    const date = row.date || "";
    const treatments = Number(row.treatments) || 0;
    const revenue = Number(row.revenue) || 0;

    if (!branchMap.has(branch)) branchMap.set(branch, new Map());
    const treatmentMap = branchMap.get(branch)!;
    if (!treatmentMap.has(treatment)) treatmentMap.set(treatment, new Map());
    const dateMap = treatmentMap.get(treatment)!;

    const existing = dateMap.get(date) || { treatments: 0, revenue: 0 };
    existing.treatments += treatments;
    existing.revenue += revenue;
    dateMap.set(date, existing);
  }

  // Sort branches alphabetically
  const sortedBranches = Array.from(branchMap.keys()).sort();

  const emptyCell = (): PivotCell => ({ treatments: 0, revenue: 0 });
  const addCell = (a: PivotCell, b: PivotCell): PivotCell => ({
    treatments: a.treatments + b.treatments,
    revenue: a.revenue + b.revenue,
  });

  const branches: BranchGroup[] = [];
  const grandTotal: Record<string, PivotCell> = {};
  let grandGrandTotal: PivotCell = emptyCell();
  allDates.forEach((d) => (grandTotal[d] = emptyCell()));

  for (const branchName of sortedBranches) {
    const treatmentMap = branchMap.get(branchName)!;
    const sortedTreatments = Array.from(treatmentMap.keys()).sort();

    const branchTotal: Record<string, PivotCell> = {};
    let branchGrandTotal: PivotCell = emptyCell();
    allDates.forEach((d) => (branchTotal[d] = emptyCell()));

    const rows: PivotRow[] = [];
    for (const treatmentName of sortedTreatments) {
      const dateMap = treatmentMap.get(treatmentName)!;
      const cells: Record<string, PivotCell> = {};
      let rowGrandTotal: PivotCell = emptyCell();

      for (const date of allDates) {
        const cell = dateMap.get(date) || emptyCell();
        cells[date] = cell;
        rowGrandTotal = addCell(rowGrandTotal, cell);
        branchTotal[date] = addCell(branchTotal[date], cell);
        grandTotal[date] = addCell(grandTotal[date], cell);
      }

      branchGrandTotal = addCell(branchGrandTotal, rowGrandTotal);
      rows.push({ branchName, treatmentName, cells, grandTotal: rowGrandTotal });
    }

    grandGrandTotal = addCell(grandGrandTotal, branchGrandTotal);
    branches.push({ branchName, rows, branchTotal, branchGrandTotal });
  }

  return { dates: allDates, branches, grandTotal, grandGrandTotal };
}

const TreatmentMonthlyViewCPPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  const [dateFrom, setDateFrom] = useState<string>(formatDateStr(startOfMonth(new Date())));
  const [dateTo, setDateTo] = useState<string>(formatDateStr(new Date()));
  const [rawData, setRawData] = useState<Array<Record<string, any>>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPrintLoading, setIsPrintLoading] = useState(false);

  // Filter & column state
  const [showModal, setShowModal] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE);
  const [branchFilter, setBranchFilter] = useState<any>(null);
  const [dealerFilter, setDealerFilter] = useState<any>(null);
  const [categoryFilter, setCategoryFilter] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState<any>(null);
  const [segmentFilter, setSegmentFilter] = useState<any>(null);
  const [jobTypeFilter, setJobTypeFilter] = useState<any>(null);
  const [advisorFilter, setAdvisorFilter] = useState<any>(null);
  const [supervisorFilter, setSupervisorFilter] = useState<any>(null);

  const showTreatments = visibleColumns.includes("treatments");
  const showRevenue = visibleColumns.includes("revenue");
  const colsPerDate = (showTreatments ? 1 : 0) + (showRevenue ? 1 : 0);

  const externalFilters = useMemo(() => {
    const f: Record<string, any> = {};
    if (branchFilter) f.branchId = branchFilter.id || branchFilter;
    if (dealerFilter) f.dealerId = dealerFilter.id || dealerFilter;
    if (categoryFilter) f.category = categoryFilter;
    if (statusFilter) f.treatmentStatus = statusFilter;
    if (segmentFilter) f.vehicleSegment = segmentFilter;
    if (jobTypeFilter) f.jobType = jobTypeFilter;
    if (advisorFilter) f.advisorId = advisorFilter.id || advisorFilter;
    if (supervisorFilter) f.supervisorId = supervisorFilter.id || supervisorFilter;
    return f;
  }, [branchFilter, dealerFilter, categoryFilter, statusFilter, segmentFilter, jobTypeFilter, advisorFilter, supervisorFilter]);

  const activeFilterCount =
    (branchFilter ? 1 : 0) +
    (dealerFilter ? 1 : 0) +
    (categoryFilter ? 1 : 0) +
    (statusFilter ? 1 : 0) +
    (segmentFilter ? 1 : 0) +
    (jobTypeFilter ? 1 : 0) +
    (advisorFilter ? 1 : 0) +
    (supervisorFilter ? 1 : 0) +
    (visibleColumns.length !== DEFAULT_VISIBLE.length ? 1 : 0);

  const handleApply = (
    cols: string[],
    branchId: string | null,
    dealerId: string | null,
    category: string | null,
    status: string | null,
    segment: string | null,
    jobType: string | null,
    advisorId?: string | null,
    supervisorId?: string | null
  ) => {
    setVisibleColumns(cols);
    setBranchFilter(branchId || null);
    setDealerFilter(dealerId || null);
    setCategoryFilter(category || null);
    setStatusFilter(status || null);
    setSegmentFilter(segment || null);
    setJobTypeFilter(jobType || null);
    setAdvisorFilter(advisorId || null);
    setSupervisorFilter(supervisorId || null);
  };

  const isDark = useMemo(() => {
    return document.documentElement.getAttribute("data-bs-theme") === "dark";
  }, []);

  // Track usage on mount
  useEffect(() => {
    trackReportUsage("treatment-monthly-view-cp");
  }, []);

  // Fetch data
  const loadData = useCallback(() => {
    setIsLoading(true);
    setError(null);

    const request: ReportDataRequest = {
      filters: {
        dateRange: { from: dateFrom, to: dateTo },
        ...externalFilters,
      },
      page: 1,
      pageSize: 999999,
      sortBy: "date",
      sortOrder: "asc",
    };

    getReportData(REPORT_ID, request)
      .then(({ data }) => {
        setRawData(data.data);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error loading report data:", err);
        setError("Failed to load report data. Please try again.");
        setIsLoading(false);
      });
  }, [dateFrom, dateTo, externalFilters]);

  // Load data on mount and when dates/filters change
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Build pivot from raw data
  const pivot = useMemo(() => {
    if (rawData.length === 0 && !isLoading) {
      return buildPivot([], new Date(dateFrom), new Date(dateTo));
    }
    return buildPivot(rawData, new Date(dateFrom), new Date(dateTo));
  }, [rawData, dateFrom, dateTo, isLoading]);

  // Navigation
  const handleBackNavigation = () => {
    const fromPath = (location.state as any)?.from;
    if (fromPath) {
      navigate(fromPath);
      return;
    }
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/reports");
    }
  };

  const getBackButtonLabel = () => {
    const fromPath = (location.state as any)?.from;
    if (fromPath === "/" || fromPath === "/dashboard") return "Dashboard";
    if (fromPath?.includes("/reports")) return "All Reports";
    if (window.history.length > 2) return "Back";
    return "All Reports";
  };

  // CSV export
  const handleExportCsv = useCallback(() => {
    const csvRows: string[] = [];

    // Header row 1
    const header: string[] = ["Branch", "Treatment"];
    for (const date of pivot.dates) {
      if (showTreatments) header.push(`${formatDateHeader(date)} Trt`);
      if (showRevenue) header.push(`${formatDateHeader(date)} Rev`);
    }
    if (showTreatments) header.push("Grand Total Trt");
    if (showRevenue) header.push("Grand Total Rev");
    csvRows.push(header.map((h) => `"${h}"`).join(","));

    // Data rows with branch subtotals
    for (const branch of pivot.branches) {
      for (const row of branch.rows) {
        const csvRow: string[] = [row.branchName, row.treatmentName];
        for (const date of pivot.dates) {
          const cell = row.cells[date] || { treatments: 0, revenue: 0 };
          if (showTreatments) csvRow.push(String(cell.treatments));
          if (showRevenue) csvRow.push(String(cell.revenue));
        }
        if (showTreatments) csvRow.push(String(row.grandTotal.treatments));
        if (showRevenue) csvRow.push(String(row.grandTotal.revenue));
        csvRows.push(csvRow.map((v) => `"${v}"`).join(","));
      }
      // Branch subtotal row
      const subRow: string[] = [branch.branchName, "SUBTOTAL"];
      for (const date of pivot.dates) {
        const cell = branch.branchTotal[date] || { treatments: 0, revenue: 0 };
        if (showTreatments) subRow.push(String(cell.treatments));
        if (showRevenue) subRow.push(String(cell.revenue));
      }
      if (showTreatments) subRow.push(String(branch.branchGrandTotal.treatments));
      if (showRevenue) subRow.push(String(branch.branchGrandTotal.revenue));
      csvRows.push(subRow.map((v) => `"${v}"`).join(","));
    }

    // Grand total row
    const grandRow: string[] = ["GRAND TOTAL", ""];
    for (const date of pivot.dates) {
      const cell = pivot.grandTotal[date] || { treatments: 0, revenue: 0 };
      if (showTreatments) grandRow.push(String(cell.treatments));
      if (showRevenue) grandRow.push(String(cell.revenue));
    }
    if (showTreatments) grandRow.push(String(pivot.grandGrandTotal.treatments));
    if (showRevenue) grandRow.push(String(pivot.grandGrandTotal.revenue));
    csvRows.push(grandRow.map((v) => `"${v}"`).join(","));

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Monthly_View_Customer_Price_${dateFrom}_to_${dateTo}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }, [pivot, dateFrom, dateTo, showTreatments, showRevenue]);

  // Print
  const handlePrint = useCallback(async () => {
    setIsPrintLoading(true);
    try {
      const orgName = currentUser?.orgName || "";
      const orgDetails = [currentUser?.orgAddress, currentUser?.orgCity, currentUser?.orgPinCode]
        .filter(Boolean)
        .join(", ");
      const orgPhone = currentUser?.orgPhone ? `Tel: ${currentUser.orgPhone}` : "";
      const generatedAt = format(new Date(), "dd-MMM-yyyy hh:mm a");

      let dateRangeStr = "";
      try {
        const fromStr = format(new Date(dateFrom), "dd-MMM-yyyy");
        const toStr = format(new Date(dateTo), "dd-MMM-yyyy");
        dateRangeStr = `${fromStr} to ${toStr}`;
      } catch {
        dateRangeStr = `${dateFrom} to ${dateTo}`;
      }

      // Build table header
      let headerRow1 = `<th rowspan="2" style="text-align:left;min-width:120px;position:sticky;left:0;background:#f0f0f0;z-index:2;">Branch</th>`;
      headerRow1 += `<th rowspan="2" style="text-align:left;min-width:120px;position:sticky;left:120px;background:#f0f0f0;z-index:2;">Treatment</th>`;
      for (const date of pivot.dates) {
        headerRow1 += `<th colspan="${colsPerDate}" style="text-align:center;border-left:2px solid #999;">${formatDateHeader(date)}</th>`;
      }
      headerRow1 += `<th colspan="${colsPerDate}" style="text-align:center;border-left:2px solid #333;background:#e8e8e8;">Grand Total</th>`;

      let headerRow2 = "";
      for (let i = 0; i < pivot.dates.length; i++) {
        if (showTreatments) headerRow2 += `<th style="text-align:right;border-left:2px solid #999;font-size:7px;">Trt</th>`;
        if (showRevenue) headerRow2 += `<th style="text-align:right;${!showTreatments ? 'border-left:2px solid #999;' : ''}font-size:7px;">Rev</th>`;
      }
      if (showTreatments) headerRow2 += `<th style="text-align:right;border-left:2px solid #333;background:#e8e8e8;font-size:7px;">Trt</th>`;
      if (showRevenue) headerRow2 += `<th style="text-align:right;background:#e8e8e8;${!showTreatments ? 'border-left:2px solid #333;' : ''}font-size:7px;">Rev</th>`;

      // Build body rows
      let bodyRows = "";
      for (const branch of pivot.branches) {
        for (let ri = 0; ri < branch.rows.length; ri++) {
          const row = branch.rows[ri];
          const bgColor = ri % 2 === 0 ? "#fff" : "#fafafa";
          let tr = `<tr style="background:${bgColor};">`;
          if (ri === 0) {
            tr += `<td rowspan="${branch.rows.length}" style="font-weight:600;vertical-align:top;position:sticky;left:0;background:${bgColor};border-right:1px solid #ddd;">${row.branchName}</td>`;
          }
          tr += `<td style="position:sticky;left:120px;background:${bgColor};">${row.treatmentName}</td>`;
          for (const date of pivot.dates) {
            const cell = row.cells[date] || { treatments: 0, revenue: 0 };
            if (showTreatments) tr += `<td style="text-align:right;border-left:2px solid #999;">${cell.treatments || ""}</td>`;
            if (showRevenue) tr += `<td style="text-align:right;${!showTreatments ? 'border-left:2px solid #999;' : ''}">${cell.revenue ? formatCurrency(cell.revenue) : ""}</td>`;
          }
          if (showTreatments) tr += `<td style="text-align:right;border-left:2px solid #333;font-weight:600;">${row.grandTotal.treatments}</td>`;
          if (showRevenue) tr += `<td style="text-align:right;font-weight:600;${!showTreatments ? 'border-left:2px solid #333;' : ''}">${formatCurrency(row.grandTotal.revenue)}</td>`;
          tr += `</tr>`;
          bodyRows += tr;
        }
        // Branch subtotal
        let subTr = `<tr style="background:#f5f5dc;font-weight:700;border-top:1px solid #999;">`;
        subTr += `<td style="position:sticky;left:0;background:#f5f5dc;">${branch.branchName}</td>`;
        subTr += `<td style="position:sticky;left:120px;background:#f5f5dc;">Subtotal</td>`;
        for (const date of pivot.dates) {
          const cell = branch.branchTotal[date] || { treatments: 0, revenue: 0 };
          if (showTreatments) subTr += `<td style="text-align:right;border-left:2px solid #999;">${cell.treatments}</td>`;
          if (showRevenue) subTr += `<td style="text-align:right;${!showTreatments ? 'border-left:2px solid #999;' : ''}">${formatCurrency(cell.revenue)}</td>`;
        }
        if (showTreatments) subTr += `<td style="text-align:right;border-left:2px solid #333;">${branch.branchGrandTotal.treatments}</td>`;
        if (showRevenue) subTr += `<td style="text-align:right;${!showTreatments ? 'border-left:2px solid #333;' : ''}">${formatCurrency(branch.branchGrandTotal.revenue)}</td>`;
        subTr += `</tr>`;
        bodyRows += subTr;
      }

      // Grand total row
      let grandRow = `<tr style="background:#d9d9d9;font-weight:700;border-top:2px solid #333;">`;
      grandRow += `<td colspan="2" style="position:sticky;left:0;background:#d9d9d9;">GRAND TOTAL</td>`;
      for (const date of pivot.dates) {
        const cell = pivot.grandTotal[date] || { treatments: 0, revenue: 0 };
        if (showTreatments) grandRow += `<td style="text-align:right;border-left:2px solid #999;">${cell.treatments}</td>`;
        if (showRevenue) grandRow += `<td style="text-align:right;${!showTreatments ? 'border-left:2px solid #999;' : ''}">${formatCurrency(cell.revenue)}</td>`;
      }
      if (showTreatments) grandRow += `<td style="text-align:right;border-left:2px solid #333;">${pivot.grandGrandTotal.treatments}</td>`;
      if (showRevenue) grandRow += `<td style="text-align:right;${!showTreatments ? 'border-left:2px solid #333;' : ''}">${formatCurrency(pivot.grandGrandTotal.revenue)}</td>`;
      grandRow += `</tr>`;

      const html = `<!DOCTYPE html>
<html>
<head>
  <title>Monthly View - Customer Price</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 20px; color: #333; }
    .header-table { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
    .org-name { font-size: 18px; font-weight: 700; color: #1a1a2e; letter-spacing: 0.5px; }
    .org-details { font-size: 10px; color: #666; margin-top: 2px; }
    .generated { font-size: 10px; color: #888; text-align: right; }
    .divider { border-bottom: 2px solid #1a1a2e; margin: 8px 0 12px 0; }
    .report-title { font-size: 16px; font-weight: 700; color: #1a1a2e; margin-bottom: 2px; }
    .report-desc { font-size: 11px; color: #666; margin-bottom: 8px; }
    .meta-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
    .meta-table td { font-size: 11px; color: #444; }
    table.pivot-table { width: 100%; border-collapse: collapse; font-size: 8px; margin-top: 4px; }
    table.pivot-table thead th {
      padding: 4px 5px; font-size: 7px; font-weight: 700;
      text-transform: uppercase; letter-spacing: 0.3px;
      border-bottom: 2px solid #1a1a2e; color: #333; background: #f0f0f0;
    }
    table.pivot-table tbody td { padding: 3px 5px; font-size: 8px; border-bottom: 0.5px solid #ddd; color: #333; }
    .footer {
      border-top: 1px solid #ddd; padding-top: 6px; margin-top: 20px;
      display: flex; justify-content: space-between; font-size: 9px; color: #999;
    }
    @media print {
      @page { size: A4 landscape; margin: 8mm 6mm; }
      body { padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <table class="header-table">
    <tr>
      <td>
        <div class="org-name">${orgName}</div>
        ${orgDetails || orgPhone ? `<div class="org-details">${orgDetails}${orgPhone ? `<span style="margin-left:10px">${orgPhone}</span>` : ""}</div>` : ""}
      </td>
      <td class="generated">Generated: ${generatedAt}</td>
    </tr>
  </table>
  <div class="divider"></div>
  <div class="report-title">Monthly View - Customer Price</div>
  <div class="report-desc">Treatment counts and customer price revenue by branch per day</div>
  <table class="meta-table">
    <tr>
      <td><strong>Period:</strong> ${dateRangeStr}</td>
      <td style="text-align:right"><strong>Total Records:</strong> ${rawData.length}</td>
    </tr>
  </table>
  <table class="pivot-table">
    <thead>
      <tr>${headerRow1}</tr>
      <tr>${headerRow2}</tr>
    </thead>
    <tbody>
      ${bodyRows}
    </tbody>
    <tfoot>
      ${grandRow}
    </tfoot>
  </table>
  <div class="footer">
    <span>Monthly View - Customer Price | ${orgName}</span>
    <span>Printed: ${generatedAt}</span>
  </div>
  <script>window.onload = function() { window.print(); };<\/script>
</body>
</html>`;

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
      }
    } catch (err) {
      console.error("Failed to print:", err);
      alert("Failed to prepare print data. Please try again.");
    } finally {
      setIsPrintLoading(false);
    }
  }, [pivot, rawData, dateFrom, dateTo, currentUser, showTreatments, showRevenue, colsPerDate]);

  return (
    <div>
      <div className="card">
        {/* Header */}
        <div className="card-header border-0 pt-6">
          <div className="card-title">
            <h3 className="card-title align-items-start flex-column">
              <span className="card-label fw-bold fs-3 mb-1">Monthly View - Customer Price</span>
              <span className="text-muted mt-1 fw-semibold fs-7">
                Treatment counts and customer price revenue by branch per day (pivot view)
              </span>
            </h3>
          </div>
          <div className="card-toolbar">
            <button
              className="btn btn-sm btn-light-primary me-3"
              onClick={handleBackNavigation}
              title={`Back to ${getBackButtonLabel()}`}
            >
              <i className="bi bi-arrow-left fs-5 me-1"></i>
              {getBackButtonLabel()}
            </button>
            {/* Columns & Filters button */}
            <button
              className="btn btn-sm btn-light-info me-3 position-relative"
              onClick={() => setShowModal(true)}
              title="Select columns and filters"
            >
              <i className="bi bi-sliders fs-5 me-1"></i>
              Columns & Filters
              {activeFilterCount > 0 && (
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-info">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {/* Date pickers */}
            <div className="me-3 d-flex align-items-center gap-2">
              <input
                type="date"
                className="form-control form-control-sm form-control-solid"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
              <span className="text-muted">to</span>
              <input
                type="date"
                className="form-control form-control-sm form-control-solid"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            {/* Action buttons */}
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-sm btn-light-success"
                onClick={handleExportCsv}
                title="Export CSV"
                disabled={isLoading || rawData.length === 0}
              >
                <i className="bi bi-file-earmark-spreadsheet fs-5 me-1"></i>
                CSV
              </button>
              <button
                className="btn btn-sm btn-light-info"
                onClick={handlePrint}
                title="Print"
                disabled={isPrintLoading || isLoading || rawData.length === 0}
              >
                {isPrintLoading ? (
                  <Spinner animation="border" size="sm" className="me-1" />
                ) : (
                  <i className="bi bi-printer fs-5 me-1"></i>
                )}
                Print
              </button>
              <button
                className="btn btn-sm btn-light-primary"
                onClick={loadData}
                title="Refresh"
                disabled={isLoading}
              >
                <i className="bi bi-arrow-clockwise fs-5"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="card-body pt-0">
            <div className="alert alert-warning d-flex align-items-center mb-0">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </div>
          </div>
        )}

        {/* Pivot Table */}
        <div className="card-body py-4">
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
              <Spinner animation="border" variant="primary" />
              <span className="ms-3 text-muted">Loading data...</span>
            </div>
          ) : rawData.length === 0 ? (
            <div className="text-center py-12">
              <i className="bi bi-inbox fs-2x text-gray-400 mb-3 d-block"></i>
              <span className="text-gray-500 fs-6">No data found for the selected date range.</span>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="table table-bordered table-sm mb-0" style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                <thead>
                  {/* Row 1: Branch, Treatment, date groups, Grand Total */}
                  <tr style={{ backgroundColor: isDark ? "#1e1e2d" : "#f5f8fa" }}>
                    <th
                      rowSpan={2}
                      style={{
                        position: "sticky",
                        left: 0,
                        zIndex: 3,
                        backgroundColor: isDark ? "#1e1e2d" : "#f5f8fa",
                        minWidth: "130px",
                        verticalAlign: "middle",
                      }}
                    >
                      Branch
                    </th>
                    <th
                      rowSpan={2}
                      style={{
                        position: "sticky",
                        left: "130px",
                        zIndex: 3,
                        backgroundColor: isDark ? "#1e1e2d" : "#f5f8fa",
                        minWidth: "140px",
                        verticalAlign: "middle",
                        borderRight: "2px solid #dee2e6",
                      }}
                    >
                      Treatment
                    </th>
                    {pivot.dates.map((date) => (
                      <th
                        key={date}
                        colSpan={colsPerDate}
                        className="text-center"
                        style={{ borderLeft: isDark ? "2px solid #555" : "2px solid #999", fontSize: "11px" }}
                      >
                        {formatDateHeader(date)}
                      </th>
                    ))}
                    <th
                      colSpan={colsPerDate}
                      className="text-center fw-bold"
                      style={{
                        borderLeft: "2px solid #333",
                        backgroundColor: isDark ? "#2b2b40" : "#e8e8e8",
                      }}
                    >
                      Grand Total
                    </th>
                  </tr>
                  {/* Row 2: Trt/Rev sub-headers */}
                  <tr style={{ backgroundColor: isDark ? "#1e1e2d" : "#f5f8fa" }}>
                    {pivot.dates.map((date) => (
                      <React.Fragment key={date}>
                        {showTreatments && (
                          <th
                            className="text-end"
                            style={{ borderLeft: isDark ? "2px solid #555" : "2px solid #999", fontSize: "10px", fontWeight: 600 }}
                          >
                            Trt
                          </th>
                        )}
                        {showRevenue && (
                          <th
                            className="text-end"
                            style={{ fontSize: "10px", fontWeight: 600, ...((!showTreatments) ? { borderLeft: isDark ? "2px solid #555" : "2px solid #999" } : {}) }}
                          >
                            Rev
                          </th>
                        )}
                      </React.Fragment>
                    ))}
                    {showTreatments && (
                      <th
                        className="text-end"
                        style={{
                          borderLeft: "2px solid #333",
                          backgroundColor: isDark ? "#2b2b40" : "#e8e8e8",
                          fontSize: "10px",
                          fontWeight: 600,
                        }}
                      >
                        Trt
                      </th>
                    )}
                    {showRevenue && (
                      <th
                        className="text-end"
                        style={{
                          backgroundColor: isDark ? "#2b2b40" : "#e8e8e8",
                          fontSize: "10px",
                          fontWeight: 600,
                          ...((!showTreatments) ? { borderLeft: "2px solid #333" } : {}),
                        }}
                      >
                        Rev
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {pivot.branches.map((branch) => (
                    <React.Fragment key={branch.branchName}>
                      {/* Treatment rows */}
                      {branch.rows.map((row, ri) => (
                        <tr key={`${branch.branchName}-${row.treatmentName}`}>
                          {ri === 0 && (
                            <td
                              rowSpan={branch.rows.length}
                              className="fw-semibold"
                              style={{
                                position: "sticky",
                                left: 0,
                                zIndex: 1,
                                backgroundColor: isDark ? "var(--bs-card-bg)" : "#fff",
                                verticalAlign: "top",
                                borderRight: "1px solid #dee2e6",
                              }}
                            >
                              {row.branchName}
                            </td>
                          )}
                          <td
                            style={{
                              position: "sticky",
                              left: "130px",
                              zIndex: 1,
                              backgroundColor: isDark ? "var(--bs-card-bg)" : "#fff",
                              borderRight: "2px solid #dee2e6",
                            }}
                          >
                            {row.treatmentName}
                          </td>
                          {pivot.dates.map((date) => {
                            const cell = row.cells[date] || { treatments: 0, revenue: 0 };
                            return (
                              <React.Fragment key={date}>
                                {showTreatments && (
                                  <td
                                    className="text-end"
                                    style={{ borderLeft: isDark ? "2px solid #555" : "2px solid #999" }}
                                  >
                                    {cell.treatments > 0 ? formatNumber(cell.treatments) : ""}
                                  </td>
                                )}
                                {showRevenue && (
                                  <td className="text-end" style={!showTreatments ? { borderLeft: isDark ? "2px solid #555" : "2px solid #999" } : {}}>
                                    {cell.revenue > 0 ? formatCurrency(cell.revenue) : ""}
                                  </td>
                                )}
                              </React.Fragment>
                            );
                          })}
                          {showTreatments && (
                            <td
                              className="text-end fw-semibold"
                              style={{ borderLeft: "2px solid #333" }}
                            >
                              {formatNumber(row.grandTotal.treatments)}
                            </td>
                          )}
                          {showRevenue && (
                            <td className="text-end fw-semibold" style={!showTreatments ? { borderLeft: "2px solid #333" } : {}}>
                              {formatCurrency(row.grandTotal.revenue)}
                            </td>
                          )}
                        </tr>
                      ))}
                      {/* Branch subtotal */}
                      <tr
                        style={{
                          backgroundColor: isDark ? "#2b2b40" : "#f5f5dc",
                          fontWeight: 700,
                          borderTop: "1px solid #999",
                        }}
                      >
                        <td
                          style={{
                            position: "sticky",
                            left: 0,
                            zIndex: 1,
                            backgroundColor: isDark ? "#2b2b40" : "#f5f5dc",
                          }}
                        >
                          {branch.branchName}
                        </td>
                        <td
                          style={{
                            position: "sticky",
                            left: "130px",
                            zIndex: 1,
                            backgroundColor: isDark ? "#2b2b40" : "#f5f5dc",
                            borderRight: "2px solid #dee2e6",
                            fontStyle: "italic",
                          }}
                        >
                          Subtotal
                        </td>
                        {pivot.dates.map((date) => {
                          const cell = branch.branchTotal[date] || { treatments: 0, revenue: 0 };
                          return (
                            <React.Fragment key={date}>
                              {showTreatments && (
                                <td
                                  className="text-end"
                                  style={{ borderLeft: isDark ? "2px solid #555" : "2px solid #999" }}
                                >
                                  {formatNumber(cell.treatments)}
                                </td>
                              )}
                              {showRevenue && (
                                <td className="text-end" style={!showTreatments ? { borderLeft: isDark ? "2px solid #555" : "2px solid #999" } : {}}>
                                  {formatCurrency(cell.revenue)}
                                </td>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {showTreatments && (
                          <td
                            className="text-end"
                            style={{ borderLeft: "2px solid #333" }}
                          >
                            {formatNumber(branch.branchGrandTotal.treatments)}
                          </td>
                        )}
                        {showRevenue && (
                          <td className="text-end" style={!showTreatments ? { borderLeft: "2px solid #333" } : {}}>
                            {formatCurrency(branch.branchGrandTotal.revenue)}
                          </td>
                        )}
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
                {/* Grand Total Footer */}
                <tfoot>
                  <tr
                    style={{
                      backgroundColor: isDark ? "#1a1a2e" : "#d9d9d9",
                      fontWeight: 700,
                      borderTop: "2px solid #333",
                    }}
                  >
                    <td
                      colSpan={2}
                      style={{
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                        backgroundColor: isDark ? "#1a1a2e" : "#d9d9d9",
                        borderRight: "2px solid #dee2e6",
                        fontSize: "13px",
                      }}
                    >
                      GRAND TOTAL
                    </td>
                    {pivot.dates.map((date) => {
                      const cell = pivot.grandTotal[date] || { treatments: 0, revenue: 0 };
                      return (
                        <React.Fragment key={date}>
                          {showTreatments && (
                            <td
                              className="text-end"
                              style={{ borderLeft: isDark ? "2px solid #555" : "2px solid #999" }}
                            >
                              {formatNumber(cell.treatments)}
                            </td>
                          )}
                          {showRevenue && (
                            <td className="text-end" style={!showTreatments ? { borderLeft: isDark ? "2px solid #555" : "2px solid #999" } : {}}>
                              {formatCurrency(cell.revenue)}
                            </td>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {showTreatments && (
                      <td
                        className="text-end"
                        style={{ borderLeft: "2px solid #333", fontSize: "13px" }}
                      >
                        {formatNumber(pivot.grandGrandTotal.treatments)}
                      </td>
                    )}
                    {showRevenue && (
                      <td className="text-end" style={{ fontSize: "13px", ...(!showTreatments ? { borderLeft: "2px solid #333" } : {}) }}>
                        {formatCurrency(pivot.grandGrandTotal.revenue)}
                      </td>
                    )}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Column & Filter Modal */}
      <ColumnSelectorModal
        show={showModal}
        onHide={() => setShowModal(false)}
        columns={PIVOT_COLUMNS}
        visibleColumns={visibleColumns}
        defaultColumns={DEFAULT_VISIBLE}
        branchFilter={branchFilter}
        dealerFilter={dealerFilter}
        categoryFilter={categoryFilter ? { id: categoryFilter, name: categoryFilter } : null}
        statusFilter={statusFilter ? { id: statusFilter, name: statusFilter } : null}
        segmentFilter={segmentFilter ? { id: segmentFilter, name: segmentFilter } : null}
        jobTypeFilter={jobTypeFilter ? { id: jobTypeFilter, name: jobTypeFilter } : null}
        advisorFilter={advisorFilter}
        supervisorFilter={supervisorFilter}
        onApply={handleApply}
      />
    </div>
  );
};

export default TreatmentMonthlyViewCPPage;
